/**
 * Canonical JSON + the snapshot checksum (R10, architecture.md §12).
 *
 * @remarks
 * Canonical means: object keys sorted, arrays in order, no whitespace — so
 * the same value always serializes to the same bytes, on every runtime, which
 * is what lets a checksum mean anything. `RuntimeState` is JSON-serializable
 * by contract (§3.2, pinned by the round-trip suite), so the walk needs no
 * escape hatches: plain objects, arrays, strings, finite numbers, booleans.
 *
 * The checksum is FNV-1a 64-bit, implemented over BigInt — chosen because it
 * is a PURE function of the bytes (no crypto APIs, which the isomorphic
 * boundary forbids: `crypto.subtle` is async and environment-gated), it is
 * deterministic across platforms, and its job is INTEGRITY against
 * truncation/corruption, not security. A tampered save is the player's own
 * save file; the threat model is a torn write, not an adversary.
 *
 * @packageDocumentation
 */

/**
 * Serialize a JSON-safe value canonically — sorted keys, no whitespace.
 *
 * @param value - Any JSON-serializable value
 * @returns The canonical string
 */
export const toCanonicalJson = (value: unknown): string => {
	if (value === null || typeof value === 'boolean' || typeof value === 'number') {
		return JSON.stringify(value);
	}

	if (typeof value === 'string') {
		return JSON.stringify(value);
	}

	if (Array.isArray(value)) {
		return `[${value.map((element) => toCanonicalJson(element)).join(',')}]`;
	}

	if (typeof value === 'object') {
		const sortedEntries = Object.entries(value as Record<string, unknown>)
			.filter(([, entryValue]) => entryValue !== undefined)
			.sort(([leftKey], [rightKey]) => (leftKey < rightKey ? -1 : 1))
			.map(([entryKey, entryValue]) => `${JSON.stringify(entryKey)}:${toCanonicalJson(entryValue)}`);

		return `{${sortedEntries.join(',')}}`;
	}

	// Functions, symbols, undefined at the top level: not JSON, and RuntimeState
	// cannot contain them by schema — a caller handing one in is a programmer
	// error worth hearing about loudly.
	throw new Error(`toCanonicalJson: value of type "${typeof value}" is not JSON-serializable`);
};

/** FNV-1a 64-bit offset basis and prime. */
const FNV_OFFSET_BASIS = 0xcbf29ce484222325n;
const FNV_PRIME = 0x100000001b3n;
const SIXTY_FOUR_BIT_MASK = 0xffffffffffffffffn;

/**
 * FNV-1a 64-bit over a string's UTF-16 code units, as fixed-width hex.
 *
 * @param text - The canonical JSON (or any string)
 * @returns Sixteen lowercase hex characters
 */
export const fnv1a64 = (text: string): string => {
	let hash = FNV_OFFSET_BASIS;

	for (let index = 0; index < text.length; index += 1) {
		hash ^= BigInt(text.charCodeAt(index));
		hash = (hash * FNV_PRIME) & SIXTY_FOUR_BIT_MASK;
	}

	return hash.toString(16).padStart(16, '0');
};
